import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, Mail, CheckCircle, AlertCircle, Users, Eye, MousePointer, Download, Filter } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { getNotificationAnalytics, getNotificationTrends } from '../../services/notificationService';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Icon from '../../components/AppIcon';




const MetricCard = ({ icon: Icon, title, value, trend, color }) => {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className={`${color} w-12 h-12 rounded-xl flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
          </div>
        )}
      </div>
      <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
      <div className="text-3xl font-bold text-gray-900">{value}</div>
    </div>
  );
};

const NotificationAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    contentType: '',
    period: 'daily'
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    // Verify SuperUser access
    if (profile && profile?.role !== 'SuperUser') {
      navigate('/');
      return;
    }

    loadAnalytics();
    loadTrends();
  }, [profile, navigate]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams = {};
      if (filters?.startDate) filterParams.startDate = filters?.startDate;
      if (filters?.endDate) filterParams.endDate = filters?.endDate;
      if (filters?.contentType) filterParams.contentType = filters?.contentType;

      const result = await getNotificationAnalytics(filterParams);
      
      if (result?.success) {
        setAnalytics(result?.analytics);
      } else {
        setError(result?.error || 'Failed to load analytics');
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err?.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const loadTrends = async () => {
    try {
      const result = await getNotificationTrends(filters?.period, 30);
      
      if (result?.success) {
        setTrends(result?.trends || []);
      }
    } catch (err) {
      console.error('Error loading trends:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    loadAnalytics();
    loadTrends();
    setShowFilters(false);
  };

  const exportData = () => {
    if (!analytics) return;

    const csvContent = [
      ['Metric', 'Value'],
      ['Total Notifications', analytics?.overview?.totalNotifications],
      ['Delivery Rate', `${analytics?.overview?.deliveryRate}%`],
      ['Open Rate', `${analytics?.overview?.openRate}%`],
      ['Click-Through Rate', `${analytics?.overview?.clickThroughRate}%`],
      ['Failure Rate', `${analytics?.overview?.failureRate}%`],
      ['Bounce Rate', `${analytics?.overview?.bounceRate}%`]
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `notification-analytics-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const COLORS = ['#6e32b9', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Analytics</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadAnalytics}
              className="px-6 py-3 bg-gradient-to-r from-[#6e32b9] to-[#8b5cf6] text-white rounded-xl hover:shadow-lg transition-all"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
      <Header />
      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Notification Analytics Dashboard</h1>
            <p className="text-purple-200">Track delivery rates, engagement metrics, and content performance</p>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 text-white rounded-xl hover:bg-white/30 transition-all backdrop-blur-sm"
            >
              <Filter className="w-5 h-5" />
              Filters
            </button>
            <button
              onClick={exportData}
              className="flex items-center gap-2 px-4 py-2 bg-white text-purple-600 rounded-xl hover:shadow-lg transition-all"
            >
              <Download className="w-5 h-5" />
              Export
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white rounded-2xl p-6 mb-6 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Analytics</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={filters?.startDate}
                  onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={filters?.endDate}
                  onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
                <select
                  value={filters?.contentType}
                  onChange={(e) => handleFilterChange('contentType', e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All Types</option>
                  <option value="musical">Musical</option>
                  <option value="role">Role</option>
                  <option value="song">Song</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Trend Period</label>
                <select
                  value={filters?.period}
                  onChange={(e) => handleFilterChange('period', e?.target?.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setFilters({ startDate: '', endDate: '', contentType: '', period: 'daily' });
                  setShowFilters(false);
                }}
                className="px-6 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                Clear
              </button>
              <button
                onClick={applyFilters}
                className="px-6 py-2 bg-gradient-to-r from-[#6e32b9] to-[#8b5cf6] text-white rounded-xl hover:shadow-lg transition-all"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            icon={Mail}
            title="Total Sent"
            value={analytics?.overview?.totalNotifications || 0}
            trend={null}
            color="bg-blue-500"
          />
          <MetricCard
            icon={CheckCircle}
            title="Delivery Rate"
            value={`${analytics?.overview?.deliveryRate || 0}%`}
            trend={analytics?.overview?.deliveryRate >= 95 ? 'up' : 'down'}
            color="bg-green-500"
          />
          <MetricCard
            icon={Eye}
            title="Open Rate"
            value={`${analytics?.overview?.openRate || 0}%`}
            trend={analytics?.overview?.openRate >= 20 ? 'up' : 'down'}
            color="bg-purple-500"
          />
          <MetricCard
            icon={MousePointer}
            title="Click-Through Rate"
            value={`${analytics?.overview?.clickThroughRate || 0}%`}
            trend={analytics?.overview?.clickThroughRate >= 5 ? 'up' : 'down'}
            color="bg-indigo-500"
          />
        </div>

        {/* Delivery Performance Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Delivery Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Delivered', value: analytics?.overview?.deliveredNotifications || 0 },
                    { name: 'Failed', value: analytics?.overview?.failedNotifications || 0 },
                    { name: 'Bounced', value: analytics?.overview?.bouncedNotifications || 0 }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[0, 1, 2]?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{analytics?.overview?.deliveredNotifications || 0}</div>
                <div className="text-sm text-gray-600">Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{analytics?.overview?.failedNotifications || 0}</div>
                <div className="text-sm text-gray-600">Failed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{analytics?.overview?.bouncedNotifications || 0}</div>
                <div className="text-sm text-gray-600">Bounced</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Engagement Metrics</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={[
                  {
                    name: 'Engagement',
                    Delivered: analytics?.overview?.deliveredNotifications || 0,
                    Opened: analytics?.overview?.openedNotifications || 0,
                    Clicked: analytics?.overview?.clickedNotifications || 0
                  }
                ]}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Delivered" fill="#6e32b9" />
                <Bar dataKey="Opened" fill="#8b5cf6" />
                <Bar dataKey="Clicked" fill="#a78bfa" />
              </BarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{analytics?.overview?.openRate || 0}%</div>
                <div className="text-sm text-gray-600">Open Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">{analytics?.overview?.clickThroughRate || 0}%</div>
                <div className="text-sm text-gray-600">CTR</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{analytics?.overview?.failureRate || 0}%</div>
                <div className="text-sm text-gray-600">Failure Rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Performance Section */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Content Performance by Type</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Content Type</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Total Sent</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Delivery Rate</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Open Rate</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Click Rate</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Failure Rate</th>
                </tr>
              </thead>
              <tbody>
                {analytics?.contentPerformance?.map((content, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 capitalize font-medium text-gray-900">{content?.contentType}</td>
                    <td className="py-3 px-4 text-center text-gray-700">{content?.total}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-semibold ${parseFloat(content?.deliveryRate) >= 95 ? 'text-green-600' : 'text-yellow-600'}`}>
                        {content?.deliveryRate}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-semibold ${parseFloat(content?.openRate) >= 20 ? 'text-green-600' : 'text-yellow-600'}`}>
                        {content?.openRate}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-semibold ${parseFloat(content?.clickRate) >= 5 ? 'text-green-600' : 'text-yellow-600'}`}>
                        {content?.clickRate}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-semibold ${parseFloat(content?.failureRate) < 5 ? 'text-green-600' : 'text-red-600'}`}>
                        {content?.failureRate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Trends Section */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Notification Trends</h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="sent" stroke="#6e32b9" strokeWidth={2} name="Sent" />
              <Line type="monotone" dataKey="delivered" stroke="#10b981" strokeWidth={2} name="Delivered" />
              <Line type="monotone" dataKey="opened" stroke="#8b5cf6" strokeWidth={2} name="Opened" />
              <Line type="monotone" dataKey="clicked" stroke="#3b82f6" strokeWidth={2} name="Clicked" />
              <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} name="Failed" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* User Behavior Insights */}
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 mb-6">User Behavior Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Users className="w-8 h-8 text-purple-600" />
                <h4 className="text-lg font-semibold text-gray-900">Total Users</h4>
              </div>
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {analytics?.userBehavior?.totalUsers || 0}
              </div>
              <p className="text-sm text-gray-600">Registered users with notification preferences</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle className="w-8 h-8 text-green-600" />
                <h4 className="text-lg font-semibold text-gray-900">Email Enabled</h4>
              </div>
              <div className="text-3xl font-bold text-green-600 mb-2">
                {analytics?.userBehavior?.emailEnabled || 0}
              </div>
              <p className="text-sm text-gray-600">Users with email notifications on</p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <AlertCircle className="w-8 h-8 text-orange-600" />
                <h4 className="text-lg font-semibold text-gray-900">Opt-Out Rate</h4>
              </div>
              <div className="text-3xl font-bold text-orange-600 mb-2">
                {analytics?.userBehavior?.optOutRate || 0}%
              </div>
              <p className="text-sm text-gray-600">Users who disabled notifications</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationAnalyticsDashboard;